import { toast } from "sonner";
import { FileText, DownloadSimple } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useGet } from "@/hooks/useResource";
import { Loading, PageHeader, EmptyState, formatAuditTime } from "@/components/common";
import { Button } from "@/components/ui/button";

async function download(path, title) {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = title || "resource";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Could not download resource.");
  }
}

export default function Resources() {
  const { data, loading } = useGet("/client/resources");
  if (loading) {
    return <Loading label="Loading resources" />;
  }
  const items = (data || []).filter((item) => item.category === "other");

  return (
    <div>
      <PageHeader
        eyebrow="Venue & event reference"
        title="Other resources"
        description="Additional venue documents shared by the Gledswood Events Team."
      />
      {items.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No other resources yet"
          description="Venue documents published by the Events Team will appear here."
        />
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="resource-list">
          {items.map((resource) => (
            <article
              key={resource.id}
              data-testid={`resource-${resource.id}`}
              className="flex items-start gap-4 rounded-lg border border-border bg-card p-6 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-lg"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-muted">
                <FileText size={24} weight="duotone" className="text-secondary" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-heading text-xl text-primary leading-snug">{resource.title}</h2>
                {resource.description && (
                  <p
                    className="mt-2 text-sm text-muted-foreground"
                    data-testid={`resource-description-${resource.id}`}
                  >
                    {resource.description}
                  </p>
                )}
                <p className="mt-2 text-xs text-muted-foreground">
                  Published {formatAuditTime(resource.created_at)}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4 rounded-full"
                  onClick={() => download(`/client/resources/${resource.id}/file`, resource.title)}
                  data-testid={`download-resource-${resource.id}`}
                >
                  <DownloadSimple size={16} className="mr-1" /> Download
                </Button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
